import React from "react";
import PropTypes from 'prop-types';
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import Errors from "../../../utils/Errors";
import StyleableSection from "../../../components/StyleableSection";
import {Button, Grid, Typography, withStyles} from "@material-ui/core";
import SvgIcon from "@material-ui/core/SvgIcon";
import {green, red} from "@material-ui/core/colors";
import {FormattedMessage} from "react-intl";
import formatMoney from "../../../utils/formatMoney";
import DeliverTimes from "../../../constants/DeliverTimes";
import SubmissionStatuses from "../../../constants/SubmissionStatuses";
import moment from "moment";
import CheckoutActions from "../../../constants/CheckoutActions";

const styles = (theme) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 500,
  },
  form: {
    maxWidth: 500,
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  successIcon: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: 86,
    height: 86,
    margin: '0 0',
  },
  confirmationLine: {
    marginTop: theme.spacing(2),
    textAlign: 'center'
  },
  buttonContainer: {
    marginTop: 16,
    width: '100%',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fixedAmountContainer: {
    width: '100%'
  },
  amountHeader: {
    padding: 0
  },
  fixedAmount: {
    fontSize: 20
  },
  errorBanner: {
    width: '100%',
    backgroundColor: red[500],
  },
  errorBannerText: {
    fontSize: 12,
    color: 'white',
    padding: 4,
    paddingLeft: 8,
    paddingRight: 8,
    textAlign: 'center'
  },
  continueButtonContainer: {
    marginTop: theme.spacing(2)
  }
});

const mapStateToProps = (state) => {
  const {locale} = state;
  return {
    locale: locale.resolvedLocale,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({}, dispatch)
  };
};

ConfirmationForm.propTypes = {
  order: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  checkoutSettings: PropTypes.object.isRequired,
  errors: PropTypes.instanceOf(Errors).isRequired,
  onBuyAnotherCard: PropTypes.func.isRequired,
  classes: PropTypes.object,
};

function ConfirmationForm({
                            order,
                            locale,
                            checkoutSettings,
                            classes,
                            onBuyAnotherCard,
                          }) {

  function renderSuccessIcon() {
    return (
      <SvgIcon htmlColor={green[500]} viewBox={'0 0 86 86'} className={classes.successIcon}>
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLineJoin="round">
          <g id="546097_accept_approve_checkmark_done_ok_icon" transform="translate(1.000000, 1.000000)"
             stroke={green[500]} strokeWidth="2">
            <circle id="Oval" cx="42" cy="42" r="42"></circle>
            <polyline id="Path" points="26.075 40.425 39.025 53.375 61.95 30.45"></polyline>
          </g>
        </g>
      </SvgIcon>
    );
  }

  function getTitleMessageId() {
    switch (order.submissionStatus) {
      case SubmissionStatuses.RECEIVED:
        return 'confirmation.title-order-received';
      case SubmissionStatuses.COMPLETED:
      default:
        return 'confirmation.title-order-complete';
    }
  }

  function getConfirmationLine1MessageId() {
    if (order.action === CheckoutActions.NEW_CARD) {
      if (order.deliveryDetails.deliverTime === DeliverTimes.NOW) {
        switch (order.submissionStatus) {
          case SubmissionStatuses.RECEIVED:
            return 'confirmation.description-order-received-gift-sent';
          case SubmissionStatuses.COMPLETED:
          default:
            return 'confirmation.description-order-complete-gift-sent';
        }
      } else if (order.deliveryDetails.deliverTime === DeliverTimes.FUTURE) {
        return 'confirmation.description-order-complete-gift-send-later';
      }
      return 'confirmation.description-order-complete-gift-sent';
    } else if(order.action === CheckoutActions.RELOAD_CARD) {
      switch(order.submissionStatus) {
        case SubmissionStatuses.RECEIVED:
          return 'confirmation.description-reload-order-received';
        case SubmissionStatuses.COMPLETED:
        default:
          return 'confirmation.description-reload-order-complete';
      }
    }
  }

  function getConfirmationLine1MessageValues() {
    if (order.action === CheckoutActions.NEW_CARD) {
      if (order.deliveryDetails.deliverTime === DeliverTimes.NOW) {
          return {
            giftAmount: formatMoney(order.giftAmount.amount, order.giftAmount.currencyCode),
            recipientEmail: order.recipientEmail
          };
      } else if (order.deliveryDetails.deliverTime === DeliverTimes.FUTURE) {
        return {
          giftAmount: formatMoney(order.giftAmount.amount, order.giftAmount.currencyCode),
          recipientEmail: order.recipientEmail,
          deliveryDate: moment(order.deliveryDetails.deliverOn).format('MMMM Do YYYY'),
          deliveryTime: moment(order.deliveryDetails.deliverOn).format('h:mm a'),
        };
      }
    } else if(order.action === CheckoutActions.RELOAD_CARD) {
      return {
        reloadAmount: formatMoney(order.giftAmount.amount, order.giftAmount.currencyCode),
      };
    }
  }

  function getConfirmationLine2MessageId() {
    if (order.action === CheckoutActions.NEW_CARD) {
      switch (order.submissionStatus) {
        case SubmissionStatuses.RECEIVED:
          return 'confirmation.description-order-received-confirmation';
        case SubmissionStatuses.COMPLETED:
        default:
          return 'confirmation.description-order-complete-confirmation';
      }
    } else if (order.action === CheckoutActions.RELOAD_CARD) {
      switch (order.submissionStatus) {
        case SubmissionStatuses.RECEIVED:
          return 'confirmation.description-reload-order-received-confirmation';
        case SubmissionStatuses.COMPLETED:
        default:
          return 'confirmation.description-reload-order-complete-confirmation';
      }
    }
  }

  function getConfirmationLine2MessageValues() {
    if(order.action === CheckoutActions.NEW_CARD) {
      return {
        senderEmail: order.deliveryDetails.senderEmail
      };
    } else if(order.action === CheckoutActions.RELOAD_CARD) {
      return {
        newBalance: formatMoney(order.newBalance.amount, order.newBalance.currencyCode)
      };
    }
  }

  let confirmationTitleMessageId = getTitleMessageId();
  let confirmationLine1MessageId = getConfirmationLine1MessageId();
  let confirmationLine1MessageValues = getConfirmationLine1MessageValues();
  let confirmationLine2MessageId = getConfirmationLine2MessageId();
  let confirmationLine2MessageValues = getConfirmationLine2MessageValues();

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} style={{display: 'flex', justifyContent: 'center'}}>
        <StyleableSection className={classes.formContainer} section={checkoutSettings.form}>
          <Grid className={classes.form} container spacing={2}>
            <Grid item xs={12} className={classes.center}>
              {renderSuccessIcon()}
            </Grid>
            <Grid item xs={12} className={classes.center}>
              <Typography variant={'h3'}>
                <FormattedMessage id={confirmationTitleMessageId}/>
              </Typography>
              <Typography variant={'body1'} className={classes.confirmationLine}>
                <FormattedMessage id={confirmationLine1MessageId} values={confirmationLine1MessageValues}/>
              </Typography>
              <Typography variant={'body1'} className={classes.confirmationLine}>
                <FormattedMessage id={confirmationLine2MessageId} values={confirmationLine2MessageValues}/>
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.center}>
              <Button variant={'outlined'} onClick={onBuyAnotherCard}>
                <FormattedMessage id={'button.buy-another-card'}/>
              </Button>
            </Grid>
          </Grid>
        </StyleableSection>
      </Grid>
    </Grid>
  );

}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(ConfirmationForm));
